/* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-var-requires, 	@typescript-eslint/no-misused-promises    */
import React, { useEffect, useState } from 'react';
import page from 'components/next/pages/page/page';
import { NavLink, Link, Route, Routes, useParams, useBlocker } from 'react-router-dom';
import { FormInput, CustomField } from 'components/next/components/form/input';
import { inject, observer } from 'mobx-react';
import './deliveryCode.scss';
import { settings } from 'components/next/routes';
import ChainSelector from 'components/next/components/chainSelector';
import type { Components } from 'types/next-api';
import * as _ from 'lodash';
import { ReactComponent as SurprisedIcon } from '@kesko/icons/mood/icon-mood_surprise.svg';

type DeliveryCode = Components.Schemas.DeliveryCode;
import { ReactComponent as HappyIcon } from '@kesko/icons/mood/icon-mood_happy.svg';
import { ReactComponent as UnhappyIcon } from '@kesko/icons/mood/icon-mood_unhappy.svg';
import { ReactComponent as CopyIcon } from '@kesko/icons/action/icon-copy.svg';
import { ReactComponent as IconArrowRight } from '@kesko/icons/nav/icon-arrow-right.svg';
import { ReactComponent as IconDelete } from '@kesko/icons/action/icon-delete.svg';
import DeliveryCodesStore from 'stores/next/deliveryCodes';
import { Chain, StoreState } from 'enums/common';
import { getAllowedChains, getExcludedChains } from 'utils/helpers';

const promptMessage =
  'Some changes you made to this concept have not been saved. If you move away from this page, these changes will be lost. Are you sure you want to continue?';

interface DeliveryCodeProps {
  deliveryCodesStore?: DeliveryCodesStore;
  chainIds: string[];
}

type DeliveryCodeParams = {
  id: string;
  chain: Chain;
};

function onUnload(e) {
  // NOTE: many modern browsers DO NOT support custom refresh messages...
  e.preventDefault();
  e.returnValue = promptMessage;
}

const DeliveryCodePage = ({ deliveryCodesStore, chainIds }: DeliveryCodeProps) => {
  const { id, chain: chainParam } = useParams<DeliveryCodeParams>();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [deliveryCode, setDeliveryCode] = useState<DeliveryCode>(null);
  const [editedDeliveryCode, setEditedDeliveryCode] = useState<DeliveryCode>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Block routing when there's unsaved changes
  const blocker = useBlocker(unsavedChanges);
  React.useEffect(() => {
    // unload function is for page refresh / back button
    // blocker is for routing inside react router
    if (unsavedChanges) {
      window.addEventListener('beforeunload', onUnload);
    } else {
      window.removeEventListener('beforeunload', onUnload);
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
    }
  }, [blocker, unsavedChanges]);

  useEffect(() => {
    const init = async () => {
      if (deliveryCodesStore.deliveryCodes.length === 0) {
        // Load delivery codes if not already loaded
        await deliveryCodesStore.getDeliveryCodesByChain(chainIds);
      }
      const foundDeliveryCode = deliveryCodesStore.deliveryCodes.find((d) => String(d.id) === id);
      if (foundDeliveryCode) {
        setDeliveryCode(foundDeliveryCode);
        setEditedDeliveryCode({ ...foundDeliveryCode });
      }
    };
    init();

    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  const updateDeliveryCode = (updatedDeliveryCode: DeliveryCode) => {
    setEditedDeliveryCode(updatedDeliveryCode);
    setUnsavedChanges(true);
  };

  const handleSave = async () => {
    const deliveryCodeId = editedDeliveryCode.id;
    const newEditedDeliveryCode = { ...editedDeliveryCode };

    await deliveryCodesStore.updateDeliveryCode(newEditedDeliveryCode);
    setUnsavedChanges(false);
    setEditedDeliveryCode(newEditedDeliveryCode);
  };

  const handleDelete = async () => {
    if (isDeleting) return;

    const confirmMessage = `Are you sure you want to delete the delivery code "${editedDeliveryCode.name}"?\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);

    try {
      // Check if the delivery code is in use
      const inUse = await deliveryCodesStore.isDeliveryCodeInUse(editedDeliveryCode.id);

      if (inUse) {
        alert('This delivery code cannot be deleted because it is currently being used by one or more concepts.');
        setIsDeleting(false);
        return;
      }

      // Delete the delivery code
      await deliveryCodesStore.deleteDeliveryCode(editedDeliveryCode.id);

      // Navigate back to settings page
      window.location.href = settings.link.replace(/:chain/, chainParam);
    } catch (error) {
      console.error('Error deleting delivery code:', error);
      alert('Failed to delete delivery code. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleChainChange = (key: string) => {
    const deliveryCode = editedDeliveryCode;
    const chainIds = [...deliveryCode.chainIds];
    if (chainIds.includes(key) && chainIds.length === 1) {
      // do nothing, there has to be at least one chain selected
      return;
    } else if (chainIds.includes(key)) {
      _.pullAt(chainIds, chainIds.indexOf(key));
    } else {
      chainIds.push(key);
    }
    updateDeliveryCode({ ...deliveryCode, chainIds });
  };

  const renderEditorStatus = () => {
    const unsaved = unsavedChanges;
    let message = 'All changes saved!';
    let icon = <HappyIcon />;
    let statusClass = 'ok';

    if (unsaved) {
      message = 'There are unsaved changes!';
      statusClass = 'warning';
      icon = <SurprisedIcon />;
    }

    if (deliveryCodesStore.status === StoreState.error) {
      message = 'Error saving changes';
      statusClass = 'error';
      icon = <UnhappyIcon />;
    }

    return (
      <div className={`editor-status ${statusClass}`}>
        <span>
          {message} {icon}
        </span>
      </div>
    );
  };

  const renderHeader = () => {
    if (!editedDeliveryCode) {
      return null;
    }
    return (
      <header className="editor-header">
        <div className="editor-header__controls">
          <NavLink className="go-back" to={settings.link.replace(/:chain/, chainParam)}>
            <img src={require('images/arrow-back.svg').default} alt="go-back" />
          </NavLink>
          <div className="editor-header__details">
            <h3>{editedDeliveryCode.title}</h3>
            <h4>
              <span>ID: {editedDeliveryCode.id}</span>
              <span>Name: {editedDeliveryCode.name}</span>
            </h4>
          </div>
          <div className="editor-header__actions">
            {renderEditorStatus()}
            <button className="delete" onClick={handleDelete} disabled={isDeleting} title="Delete delivery code">
              <IconDelete />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
            <button className="save" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
        <div className="editor-header__details" />
      </header>
    );
  };

  const renderDeliveryCodeContent = () => {
    const deliveryCode = editedDeliveryCode;
    const chainIds = deliveryCode.chainIds;
    return (
      <div className="content">
        <FormInput
          type="text"
          label="Name"
          value={deliveryCode.name || ''}
          required={true}
          handleChange={(e) => updateDeliveryCode({ ...deliveryCode, name: e.target.value })}
        />
        <FormInput
          type="text"
          label="Title"
          value={deliveryCode.title || ''}
          required={true}
          handleChange={(e) => updateDeliveryCode({ ...deliveryCode, title: e.target.value })}
        />
        <FormInput
          type="number"
          label="Order"
          value={deliveryCode.order || 0}
          min={0}
          handleChange={(e) => updateDeliveryCode({ ...deliveryCode, order: Number(e.target.value) })}
        />
        <CustomField>
          <ChainSelector
            chainSelection={chainIds || []}
            handleChainChange={handleChainChange}
            label="Chain(s)"
            detail="Pick chains for deliveries"
          />
        </CustomField>
        <CustomField>
          <label className="input-field">
            <span className="input-field__label">Mandatory</span>
            <input
              type="checkbox"
              checked={deliveryCode.mandatory || false}
              onChange={(e) => updateDeliveryCode({ ...deliveryCode, mandatory: e.target.checked })}
            />
            <span className="input-field__detail">Is this delivery code mandatory for concepts</span>
          </label>
        </CustomField>
      </div>
    );
  };

  if (!editedDeliveryCode) {
    return null;
  }

  const renderConfirmation = () => {
    if (blocker) {
      if (blocker.state === 'blocked') {
        const confirmed = window.confirm(promptMessage);
        if (confirmed) {
          blocker.proceed?.();
        } else {
          blocker.reset?.();
        }
      }
    }
  };

  return (
    <>
      {renderConfirmation()}
      <div className="delivery-code-page">
        {renderHeader()}
        <Routes>
          <Route path="*" element={renderDeliveryCodeContent()} />
        </Routes>
      </div>
    </>
  );
};

export default page(inject('deliveryCodesStore')(observer(DeliveryCodePage)));
